const isLocalStorageAvailable = () => {
  try {
    localStorage.setItem('test_key', 'storage_test');
    localStorage.removeItem('test_key');
    return true;
  } catch (_error) {
    return false;
  }
};

type TSSOData = {
  accessToken?: string | null;
  refreshToken?: string | null;
  idToken?: string | null;
  upn?: string | null;
  cao?: string | null;
  error?: string | null;
};

export type TStorageData = {
  id?: string;
  state?: string;
  sso?: TSSOData | null;
};

const inMemoryStorageService = () => {
  let inMemoryStorage = {};

  return {
    get: (key: string, callback?: (...args: any[]) => void) => {
      const storageItem = inMemoryStorage[key];
      if (storageItem) {
        return storageItem;
      }
      if (typeof callback === 'function') {
        return callback();
      }
      return storageItem;
    },
    set: (key: string, value: TStorageData) => {
      if (value) {
        inMemoryStorage[key] = JSON.stringify(value);
      }
    },
    delete: (key: string) => {
      inMemoryStorage[key] = undefined;
    },
    clear: () => {
      inMemoryStorage = {};
    },
  };
};

const localStorageService = () => ({
  get: (key: string, callback?: (...args: any[]) => void) => {
    const storageItem = localStorage.getItem(key);
    if (storageItem) {
      return storageItem;
    }
    if (typeof callback === 'function') {
      return callback();
    }
    return storageItem;
  },
  set: (key: string, value: TStorageData) => {
    if (value) {
      localStorage.setItem(key, JSON.stringify(value));
    }
  },
  delete: (key: string) => {
    localStorage.removeItem(key);
  },
  clear: () => {
    localStorage.clear();
  },
});

// In private browser mode ( incognito mode ), localStorage is not available
const storageService = isLocalStorageAvailable()
  ? localStorageService()
  : inMemoryStorageService();

// Before introducing the config specific storage key, we used 'id' and 'state' as the storage keys.
// This function is to remove the old storage items.
const removeOldStorageItems = () => {
  storageService.delete('id');
  storageService.delete('state');
};

export const getUserId = (configId: string) => {
  try {
    removeOldStorageItems();

    const storageItem = storageService?.get(configId);
    if (storageItem) {
      const parsedData = JSON.parse(storageItem);
      return parsedData.id;
    }
    return null;
  } catch (_error) {
    storageService?.delete(configId);
    return null;
  }
};

export const getStorage = (configId: string): TStorageData => {
  try {
    const raw = storageService.get(configId);

    if (!raw) {
      return {};
    }

    return JSON.parse(raw) as TStorageData;
  } catch {
    return {};
  }
};

export const patchStorage = (
  configId: string,
  data: Partial<TStorageData>,
): void => {
  const current = getStorage(configId);
  storageService.set(configId, { ...current, ...data });
};

// In private browser mode ( incognito mode ), localStorage is not available
export default storageService;
